import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Clock, Loader2, LogIn, Mail, Moon, Sun, XCircle } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";

// The passwordless auth surfaces — email → magic link → session. ONE design serves both audiences (the
// client portal and OW staff); only a hint line differs, so the two logins read as the same product.
// LoginPage runs entry → "check your email" locally; AuthCallbackPage is the screen the emailed link lands
// on, its states (verifying / signed-in / expired / invalid) addressable by `?state=` for preview. The card
// is CENTRED — wordmark, status badge, heading and copy all on the centre line — and EVERY state leads with
// the same badge so the top never reads empty. Dev wires the real send + token verify + session; no passwords.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Variant = "client" | "staff";
const HINT: Record<Variant, string> = {
  client: "Use the work email from your engagement.",
  staff: "Sign in with your @oliverwyman.com email.",
};

/** The co-brand wordmark — the same "OW × Client" lockup as the top bar, centred at the top of the
 *  auth card as the brand moment. */
function Wordmark() {
  return (
    <span className="text-lg font-semibold tracking-tight text-foreground">
      OW <span className="font-normal text-muted-foreground">×</span> Client
    </span>
  );
}

/** A quiet theme toggle in the corner — the auth screens live outside the app shell (no account menu), so
 *  this keeps light/dark reachable before sign-in. */
function ThemeCorner() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${dark ? "light" : "dark"} mode`}
      className="fixed right-4 top-4 grid size-9 place-items-center rounded-lg text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
    >
      {dark ? <Moon className="size-[18px]" /> : <Sun className="size-[18px]" />}
    </button>
  );
}

/** The shared auth card — a slim brand accent, the wordmark centred at the top, then the screen's content,
 *  with one support line beneath. Every auth screen renders through this, centred throughout, so entry,
 *  "check your email", and the link-landing states are unmistakably one flow. */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-5 py-10">
      <ThemeCorner />
      <div className="w-full max-w-[400px]">
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="h-1 bg-[var(--brand-accent)]" aria-hidden />
          <div className="p-6 sm:p-8">
            <div className="mb-7 text-center">
              <Wordmark />
            </div>
            {children}
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Trouble signing in?{" "}
          <a
            href="mailto:portal@oliverwyman.com"
            className="font-medium text-foreground underline-offset-2 hover:underline"
          >
            Contact your OW team
          </a>
        </p>
      </div>
    </div>
  );
}

/** A toned status badge — a soft tint of a semantic colour with the icon in that colour (or a neutral badge
 *  when no `tone` is given). Centred (mx-auto); colour is always paired with the icon + heading below it. */
function StatusBadge({ tone, children }: { tone?: string; children: ReactNode }) {
  return (
    <div
      className={cn("mx-auto grid size-12 place-items-center rounded-full", !tone && "bg-muted text-foreground")}
      style={tone ? { backgroundColor: `color-mix(in oklab, ${tone} 14%, transparent)`, color: tone } : undefined}
    >
      {children}
    </div>
  );
}

/** Sign in — passwordless. `variant` only swaps the hint line, so the client and staff logins are the same
 *  screen. Entering a valid email advances to the local "check your email" state. */
export function LoginPage({ variant = "client" }: { variant?: Variant }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const valid = EMAIL_RE.test(email.trim());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) setSent(true);
  };

  return (
    <AuthShell>
      {sent ? (
        <div className="space-y-5">
          <StatusBadge tone="var(--color-info)">
            <Mail className="size-6" aria-hidden />
          </StatusBadge>
          <div className="space-y-1.5 text-center">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Check your email</h1>
            {/* Two complete thoughts on two lines — split at the sentence, never mid-phrase. */}
            <p className="text-sm leading-relaxed text-muted-foreground">
              We sent a sign-in link to <span className="font-medium text-foreground">{email.trim()}</span>.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">The link expires in 15 minutes.</p>
          </div>
          <div className="space-y-2">
            <Button type="button" onClick={() => setSent(true)} variant="outline" size="lg" className="w-full">
              Resend link
            </Button>
            <Button type="button" onClick={() => setSent(false)} variant="ghost" size="lg" className="w-full">
              Use a different email
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">Can't find it? Check your spam folder.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <StatusBadge>
            <LogIn className="size-6" aria-hidden />
          </StatusBadge>
          <div className="space-y-1.5 text-center">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Sign in</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">We'll email you a secure sign-in link.</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Input
              type="email"
              aria-label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-10 text-center"
            />
            <Button type="submit" disabled={!valid} size="lg" className="w-full">
              <Mail /> Email me a sign-in link
            </Button>
          </form>
          <p className="text-center text-xs text-muted-foreground">{HINT[variant]}</p>
        </div>
      )}
    </AuthShell>
  );
}

/** The four states the emailed link can land on. `verifying` is the transient default (token being checked);
 *  the rest are terminal. Each pairs a semantic colour with an icon + heading so the outcome is unmistakable. */
export type CallbackState = "verifying" | "success" | "expired" | "invalid";

/** The screen the magic link opens — completes the flow past "check your email". In production the token in
 *  the URL decides the state; here it's driven by `?state=` so each outcome is previewable. */
export function AuthCallbackPage({ state = "verifying" }: { state?: CallbackState }) {
  if (state === "verifying") {
    return (
      <AuthShell>
        <div className="space-y-4 text-center">
          <StatusBadge>
            <Loader2 className="size-6 animate-spin" aria-hidden />
          </StatusBadge>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Signing you in…</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">This will only take a moment.</p>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (state === "success") {
    return (
      <AuthShell>
        <div className="space-y-5 text-center">
          <StatusBadge tone="var(--color-success)">
            <CheckCircle2 className="size-6" aria-hidden />
          </StatusBadge>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">You're signed in</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">Taking you to the portal…</p>
          </div>
          <Button asChild variant="outline" size="lg" className="w-full">
            <Link to="/">Continue to the portal</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (state === "expired") {
    return (
      <AuthShell>
        <div className="space-y-5 text-center">
          <StatusBadge tone="var(--color-warning)">
            <Clock className="size-6" aria-hidden />
          </StatusBadge>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">This link has expired</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Sign-in links expire after 15 minutes. Request a new one to continue.
            </p>
          </div>
          <Button asChild size="lg" className="w-full">
            <Link to="/login">
              <Mail /> Request a new link
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="space-y-5 text-center">
        <StatusBadge tone="var(--color-destructive)">
          <XCircle className="size-6" aria-hidden />
        </StatusBadge>
        <div className="space-y-1.5">
          <h1 className="text-lg font-semibold tracking-tight text-foreground">This link isn't valid</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            It may have already been used. Request a new sign-in link to continue.
          </p>
        </div>
        <Button asChild size="lg" className="w-full">
          <Link to="/login">
            <Mail /> Request a new link
          </Link>
        </Button>
      </div>
    </AuthShell>
  );
}
